import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { makeAutoObservable, observable, ObservableMap } from 'mobx'
import { defaultMobxCacheMaxAge, type RootStore } from 'stores/RootStore'
import { MinimalUserSerializerUser } from '../types'
import { makePersistable } from 'mobx-persist-store'

type UserId = number
type Url = string
type User = MinimalUserSerializerUser

export class UsersStore {
  rootStore: RootStore
  users: ObservableMap<UserId, User> = observable.map()
  requests: ObservableMap<Url, Promise<User>> = observable.map()

  constructor(rootStore: RootStore, shouldPersist = true) {
    this.rootStore = rootStore
    makeAutoObservable(this)

    if (shouldPersist) {
      makePersistable(this, {
        name: 'UsersStore',
        properties: ['users'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }
  }

  add = (user: User) => {
    this.users.set(user.id, user)
  }

  bulkAdd = (users: User[]) => {
    users.forEach(this.add)
  }

  get = (id: UserId) => this.users.get(id)

  fetch = (id: UserId) => {
    return new Promise<User>((resolve, reject) => {
      const user = this.users.get(id)
      if (user) {
        resolve(user)
        return
      }
      const url = `/users/${id}/card`

      let request = this.requests.get(url)
      if (!request) {
        request = sendToApi<{ user: User }>(url).then((res) => {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            throw new Error()
          }
          this.users.set(id, res.data.user)
          return res.data.user
        })
        this.requests.set(url, request)
      }
      request
        .then(resolve)
        .catch(reject)
        .finally(() => this.requests.delete(url))
    })
  }
}
